import { GameState, GameStats, AttackHitbox } from './types'
import { Player } from './Player'
import { Boss } from './Boss'
import { ParticleSystem } from './Particle'
import { AudioManager } from './Audio'

export class GameEngine {
  private canvas: HTMLCanvasElement
  private ctx: CanvasRenderingContext2D
  private player: Player
  private boss: Boss
  private particles: ParticleSystem
  private audio: AudioManager

  private gameState: GameState = 'menu'
  private stats: GameStats = {
    combo: 0,
    maxCombo: 0,
    score: 0,
  }

  private groundY = 550
  private lastTime = 0
  private comboTimer = 0
  private screenShake = 0
  private cameraOffset = { x: 0, y: 0 }

  private forestOffset = 0
  private cloudOffset = 0

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas
    this.ctx = canvas.getContext('2d')!
    this.ctx.imageSmoothingEnabled = false

    this.player = new Player(100, 400)
    this.boss = new Boss(600, 400)
    this.particles = new ParticleSystem()
    this.audio = new AudioManager()
    this.audio.initialize()
  }

  start() {
    this.gameState = 'playing'
    this.audio.startMusic(0.5)
    this.gameLoop(0)
  }

  startBossFight() {
    this.gameState = 'boss'
    this.boss.reset(600, 400)
    this.audio.updateIntensity(1.5)
  }

  private gameLoop = (currentTime: number) => {
    const deltaTime = Math.min((currentTime - this.lastTime) / 1000, 0.1)
    this.lastTime = currentTime

    if (this.gameState === 'playing' || this.gameState === 'boss') {
      this.update(deltaTime)
    }

    this.render()
    requestAnimationFrame(this.gameLoop)
  }

  private update(deltaTime: number) {
    this.comboTimer = Math.max(0, this.comboTimer - deltaTime)
    if (this.comboTimer <= 0 && this.stats.combo > 0) {
      this.stats.combo = 0
    }

    const playerAttack = this.player.update(deltaTime, this.groundY)

    let bossAttack: AttackHitbox | null = null
    if (this.gameState === 'boss') {
      bossAttack = this.boss.update(
        deltaTime,
        this.player.position,
        this.groundY,
      )
    }

    if (playerAttack && this.gameState === 'boss') {
      if (this.checkCollision(playerAttack, this.boss)) {
        const defeated = this.boss.takeDamage(playerAttack.damage)
        this.stats.combo++
        this.stats.maxCombo = Math.max(this.stats.maxCombo, this.stats.combo)
        this.stats.score += 100 * this.stats.combo
        this.comboTimer = 2

        this.particles.createExplosion(
          {
            x: this.boss.position.x + this.boss.width / 2,
            y: this.boss.position.y + this.boss.height / 2,
          },
          'rgb(255, 100, 100)',
          15,
        )

        this.screenShake = 0.3
        this.audio.playHit()
        this.audio.playCombo()

        if (defeated) {
          this.gameState = 'victory'
          this.audio.stopMusic()
        }
      }
    }

    if (bossAttack) {
      if (this.checkCollision(bossAttack, this.player)) {
        const defeated = this.player.takeDamage(bossAttack.damage)
        this.stats.combo = 0

        this.particles.createExplosion(
          {
            x: this.player.position.x + this.player.width / 2,
            y: this.player.position.y + this.player.height / 2,
          },
          'rgb(100, 150, 255)',
          10,
        )

        this.screenShake = 0.5
        this.audio.playHit()

        if (defeated) {
          this.gameState = 'gameover'
          this.audio.stopMusic()
        }
      }
    }

    this.particles.update(deltaTime)

    if (Math.abs(this.player.velocity.x) > 2) {
      this.particles.createTrail(
        {
          x: this.player.position.x + this.player.width / 2,
          y: this.player.position.y + this.player.height,
        },
        'rgba(74, 158, 255, 0.5)',
      )
    }

    // Continuous forest scrolling
    this.forestOffset -= this.player.velocity.x * deltaTime * 15
    this.cloudOffset -= this.player.velocity.x * deltaTime * 5

    if (this.screenShake > 0) {
      this.screenShake -= deltaTime
      this.cameraOffset = {
        x: (Math.random() - 0.5) * 10 * this.screenShake,
        y: (Math.random() - 0.5) * 10 * this.screenShake,
      }
    } else {
      this.cameraOffset = { x: 0, y: 0 }
    }

    const intensity = 0.5 + this.stats.combo / 20
    this.audio.updateIntensity(Math.min(intensity, 2))
  }

  private checkCollision(
    hitbox: AttackHitbox,
    entity: {
      position: { x: number; y: number }
      width: number
      height: number
    },
  ): boolean {
    return (
      hitbox.position.x < entity.position.x + entity.width &&
      hitbox.position.x + hitbox.width > entity.position.x &&
      hitbox.position.y < entity.position.y + entity.height &&
      hitbox.position.y + hitbox.height > entity.position.y
    )
  }

  private drawTree(x: number, y: number, size: number) {
    // Tree trunk
    this.ctx.fillStyle = '#4a3728'
    this.ctx.fillRect(x + size * 0.4, y + size * 0.5, size * 0.2, size * 0.5)

    // Tree foliage (3 layers)
    this.ctx.fillStyle = '#2d5016'
    this.ctx.fillRect(x, y + size * 0.3, size, size * 0.3)
    this.ctx.fillStyle = '#3a6b1f'
    this.ctx.fillRect(x + size * 0.1, y + size * 0.15, size * 0.8, size * 0.25)
    this.ctx.fillStyle = '#4a8028'
    this.ctx.fillRect(x + size * 0.2, y, size * 0.6, size * 0.2)
  }

  private drawBush(x: number, y: number, size: number) {
    this.ctx.fillStyle = '#2d5016'
    this.ctx.fillRect(x, y, size, size * 0.6)
    this.ctx.fillStyle = '#3a6b1f'
    this.ctx.fillRect(x + size * 0.2, y - size * 0.2, size * 0.6, size * 0.4)
  }

  private drawCloud(x: number, y: number, size: number) {
    this.ctx.fillStyle = 'rgba(200, 200, 220, 0.6)'
    this.ctx.fillRect(x, y + size * 0.3, size, size * 0.4)
    this.ctx.fillRect(x + size * 0.2, y, size * 0.6, size * 0.6)
    this.ctx.fillRect(x + size * 0.5, y + size * 0.2, size * 0.5, size * 0.5)
  }

  private render() {
    this.ctx.save()
    this.ctx.translate(this.cameraOffset.x, this.cameraOffset.y)

    // Sky gradient
    const skyGradient = this.ctx.createLinearGradient(0, 0, 0, this.groundY)
    skyGradient.addColorStop(0, '#1a2332')
    skyGradient.addColorStop(0.5, '#2d3f5f')
    skyGradient.addColorStop(1, '#3d5a80')
    this.ctx.fillStyle = skyGradient
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height)

    // Clouds (far background)
    for (let i = -2; i < 10; i++) {
      const x = ((i * 150 + this.cloudOffset) % (this.canvas.width + 200)) - 100
      this.drawCloud(x, 50 + (i % 3) * 30, 40 + (i % 2) * 20)
    }

    // Distant trees (parallax layer 1)
    for (let i = -2; i < 12; i++) {
      const x =
        ((i * 120 + this.forestOffset * 0.3) % (this.canvas.width + 150)) - 75
      this.ctx.globalAlpha = 0.4
      this.drawTree(x, this.groundY - 180, 60)
    }
    this.ctx.globalAlpha = 1

    // Mid-distance trees (parallax layer 2)
    for (let i = -2; i < 15; i++) {
      const x =
        ((i * 90 + this.forestOffset * 0.6) % (this.canvas.width + 120)) - 60
      this.ctx.globalAlpha = 0.6
      this.drawTree(x, this.groundY - 140, 50)
    }
    this.ctx.globalAlpha = 1

    // Foreground trees
    for (let i = -2; i < 20; i++) {
      const x = ((i * 60 + this.forestOffset) % (this.canvas.width + 80)) - 40
      this.drawTree(x, this.groundY - 100, 40)
    }

    // Bushes
    for (let i = -2; i < 25; i++) {
      const x =
        ((i * 50 + this.forestOffset * 1.2) % (this.canvas.width + 70)) - 35
      this.drawBush(x, this.groundY - 30, 25)
    }

    // Ground
    this.ctx.fillStyle = '#1a4d2e'
    this.ctx.fillRect(
      0,
      this.groundY,
      this.canvas.width,
      this.canvas.height - this.groundY,
    )

    // Grass details
    this.ctx.fillStyle = '#2d6b3f'
    for (let i = -2; i < 50; i++) {
      const x =
        ((i * 20 + this.forestOffset * 1.5) % (this.canvas.width + 30)) - 15
      this.ctx.fillRect(x, this.groundY, 8, 8)
      this.ctx.fillRect(x + 4, this.groundY - 4, 4, 4)
    }

    // Dynamic lighting (spotlight on player)
    const gradient = this.ctx.createRadialGradient(
      this.player.position.x + this.player.width / 2,
      this.player.position.y + this.player.height / 2,
      0,
      this.player.position.x + this.player.width / 2,
      this.player.position.y + this.player.height / 2,
      200,
    )
    gradient.addColorStop(0, 'rgba(255, 255, 200, 0.15)')
    gradient.addColorStop(1, 'rgba(255, 255, 200, 0)')
    this.ctx.fillStyle = gradient
    this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height)

    this.player.render(this.ctx)

    if (this.gameState === 'boss' || this.gameState === 'victory') {
      this.boss.render(this.ctx)

      const bossGradient = this.ctx.createRadialGradient(
        this.boss.position.x + this.boss.width / 2,
        this.boss.position.y + this.boss.height / 2,
        0,
        this.boss.position.x + this.boss.width / 2,
        this.boss.position.y + this.boss.height / 2,
        150,
      )
      bossGradient.addColorStop(0, 'rgba(255, 50, 50, 0.2)')
      bossGradient.addColorStop(1, 'rgba(255, 50, 50, 0)')
      this.ctx.fillStyle = bossGradient
      this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height)
    }

    this.particles.render(this.ctx)

    this.ctx.restore()
  }

  getState(): {
    gameState: GameState
    stats: GameStats
    player: Player
    boss: Boss
  } {
    return {
      gameState: this.gameState,
      stats: this.stats,
      player: this.player,
      boss: this.boss,
    }
  }

  reset() {
    this.player.reset(100, 400)
    this.boss.reset(600, 400)
    this.stats = { combo: 0, maxCombo: 0, score: 0 }
    this.particles.clear()
    this.gameState = 'menu'
    this.audio.stopMusic()
  }

  setState(state: GameState) {
    this.gameState = state
  }
}
